export function getHeaderComponent(template) {
  return template?.components?.find((c) => c?.type === 'HEADER') || null;
}

function countBodyVariables(bodyText = '') {
  const matches = bodyText.match(/\{\{\d+\}\}/g) || [];
  if (!matches.length) return 0;
  return matches.reduce((max, match) => {
    const num = parseInt(match.replace(/[{}]/g, ''), 10);
    return Number.isFinite(num) ? Math.max(max, num) : max;
  }, 0);
}

export function getBodyParamLabels(template) {
  const bodyObj = template?.components?.find((c) => c?.type === 'BODY');
  const bodyText = bodyObj?.text || '';
  const exampleLabels = bodyObj?.example?.body_text?.[0] || [];
  const paramLabels = Array.isArray(template?.param) ? template.param : [];

  const variableCount = Math.max(
    countBodyVariables(bodyText),
    exampleLabels.length,
    paramLabels.length
  );

  if (!variableCount) return [];

  return Array.from({ length: variableCount }, (_, index) => {
    const fromBodyExample = `${exampleLabels[index] ?? ''}`.trim();
    const fromParam = `${paramLabels[index] ?? ''}`.trim();
    return fromBodyExample || fromParam || `Parameter ${index + 1}`;
  });
}

/**
 * Bulk upload sheet column headers — mirrors backend get_template_sheet_params:
 * template.param first, then BODY example.body_text fallback.
 */
export function getTemplateSheetParamColumns(template) {
  if (!template) return [];

  const paramLabels = Array.isArray(template?.param) ? template.param : [];
  if (paramLabels.length) {
    return paramLabels.map((label) => String(label ?? '').trim()).filter(Boolean);
  }

  const bodyObj = template?.components?.find((c) => c?.type === 'BODY');
  const exampleLabels = bodyObj?.example?.body_text?.[0] || [];
  if (exampleLabels.length && exampleLabels.some((label) => String(label ?? '').trim())) {
    return exampleLabels.map((label) => String(label ?? '').trim());
  }

  return getBodyParamLabels(template);
}

export function initBodyParams(template) {
  const labels = getBodyParamLabels(template);
  if (!labels.length) return [];
  return labels.map((label) => ({ type: 'text', text: '', label }));
}

export function initButtonUrlParams(template) {
  const buttonObj = template?.components?.find((c) => c?.type === 'BUTTONS');
  if (!buttonObj?.buttons?.length) return [];
  return buttonObj.buttons.map(() => null);
}

export function getButtonsWithExamples(template) {
  const buttonObj = template?.components?.find((c) => c?.type === 'BUTTONS');
  if (!buttonObj?.buttons?.length) return [];
  return buttonObj.buttons
    .map((btn, originalIndex) => ({ ...btn, originalIndex }))
    .filter((btn) => Array.isArray(btn?.example) && btn.example.length > 0);
}

export function createButtonParamsObj(buttonUrlParams = []) {
  return buttonUrlParams
    .map((item, index) => {
      if (item !== null && item !== undefined) {
        return {
          type: 'button',
          index: `${index}`,
          sub_type: 'url',
          parameters: [{ type: 'text', text: item }],
        };
      }
      return null;
    })
    .filter(Boolean);
}

export function buildSendTemplatePayload({
  formDataToSend,
  template,
  bodyParams,
  headerComponent,
  headerFileUrl,
  headerFileName,
  useCreateImage,
  buttonUrlParams,
}) {
  const data = {
    ...formDataToSend,
    ts_id: template?.ts_id || '',
    name: template?.name,
    code: template?.language,
    components: [{ type: 'body', parameters: bodyParams }],
  };

  if (headerComponent) {
    const objToAdd = { type: 'header' };

    if (useCreateImage && template?.media?.header) {
      const formatType = headerComponent.format?.toLowerCase();
      const mediaUrl = template.media.header;
      const rawName = mediaUrl.split('?')[0].split('/').pop() || '';
      const afterUnderscore = rawName.includes('_') ? rawName.slice(rawName.indexOf('_') + 1) : rawName;
      let filenameFromUrl = afterUnderscore;
      try {
        filenameFromUrl = decodeURIComponent(afterUnderscore);
      } catch (_) {}

      objToAdd.parameters = [{
        type: formatType,
        caption: filenameFromUrl || template?.media?.headerFilename || `header.${formatType}`,
        link: mediaUrl,
      }];
    } else if (headerFileUrl) {
      objToAdd.parameters = [{
        type: headerComponent.format?.toLowerCase(),
        caption: headerFileName ?? '',
        link: headerFileUrl,
      }];
    } else if (headerComponent.format === 'TEXT') {
      objToAdd.parameters = [{ type: 'text', text: headerComponent.text || 'header text' }];
    }

    if (objToAdd.parameters) data.components.push(objToAdd);
  }

  const buttonParams = createButtonParamsObj(buttonUrlParams);
  if (buttonParams.length) data.components.push(...buttonParams);

  return data;
}

export function parseTemplateSendError(response) {
  const root = response?.data ?? response;
  const errorData = root?.data ?? root;

  const generalError = errorData?.general_error;
  if (generalError !== undefined && generalError !== null) {
    if (typeof generalError === 'string') return generalError;

    if (Array.isArray(generalError)) {
      const messages = generalError
        .map((item) => {
          if (typeof item === 'string') return item;
          return item?.message || item?.codeMsg || item?.error?.message || '';
        })
        .filter(Boolean);
      if (messages.length) return messages.join(', ');
    }

    const metaMessage = generalError?.error?.message;
    if (metaMessage) {
      const details = generalError?.error?.error_data?.details;
      return details ? `${metaMessage} — ${details}` : metaMessage;
    }

    if (generalError?.message) return generalError.message;
    if (generalError?.errors) {
      return Array.isArray(generalError.errors)
        ? generalError.errors.join(', ')
        : String(generalError.errors);
    }
  }

  if (typeof errorData === 'string') return errorData;
  if (errorData?.message) return errorData.message;

  return 'Failed to send template';
}

function formatBulkErrors(errors) {
  if (Array.isArray(errors)) return errors.join('\n');
  if (typeof errors === 'string') return errors;
  return JSON.stringify(errors);
}

export function parseBulkUploadError(response) {
  const root = response?.data ?? response;
  const errorData = root?.data ?? root;
  const payload = errorData?.general_error ?? errorData;

  if (payload?.errors) {
    return formatBulkErrors(payload.errors);
  }

  if (typeof payload === 'string') return payload;
  if (typeof errorData?.errors === 'string') return errorData.errors;

  return parseTemplateSendError(response) || 'Failed to upload file';
}

export function getBodyPreviewText(template) {
  const body = template?.components?.find((c) => c?.type === 'BODY');
  if (!body?.text) return '';
  if (body.example?.body_text?.[0]?.length) {
    return body.text.replace(/{{(\d+)}}/g, (match, number) => {
      const index = parseInt(number, 10) - 1;
      return body.example.body_text[0][index] || match;
    });
  }
  return body.text;
}
